import DocumentFieldGrid from './DocumentFieldGrid'
import DocumentSection from './DocumentSection'
import SignatureBlock from './SignatureBlock'
import VehicleDocumentTable from './VehicleDocumentTable'
import { DOCUMENT_FALLBACKS } from './documentFallbacks'
import { formatAddress, formatDate, formatPhone, safeText, safeVin, yesNo } from './documentFormatters'

interface BillOfLadingViewProps {
  load: any
  job?: any
}

export default function BillOfLadingView({ load, job }: BillOfLadingViewProps) {
  const loadRow = load ?? {}
  const jobRow = job ?? {}
  const vehicles = Array.isArray(loadRow.vehicles) ? loadRow.vehicles : []

  return (
    <div className="space-y-4 text-slate-900">
      <DocumentSection title="Header" subtitle="Bill of lading / transport order for pickup and delivery verification.">
        <DocumentFieldGrid
          items={[
            { label: 'Order Number', value: safeText(jobRow.job_number ?? jobRow.jobNumber ?? loadRow.load_number ?? loadRow.loadNumber ?? jobRow.id, DOCUMENT_FALLBACKS.unknown) },
            { label: 'Date', value: formatDate(jobRow.assignedAt ?? loadRow.createdAt ?? loadRow.created_at) },
            { label: 'Carrier', value: safeText(loadRow.carrier_name ?? loadRow.carrierName ?? jobRow.carrier_name ?? jobRow.carrierName ?? loadRow.carrier_org_id ?? jobRow.carrier_org_id, DOCUMENT_FALLBACKS.notAssigned) },
            { label: 'MC Number', value: safeText(loadRow.carrier_mc_number ?? jobRow.carrier_mc_number ?? loadRow.mcNumber ?? jobRow.mcNumber, DOCUMENT_FALLBACKS.tbd) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Origin" subtitle="Pickup or shipper information.">
        <DocumentFieldGrid
          items={[
            { label: 'Pickup Name', value: safeText(loadRow.pickup_contact ?? loadRow.origin_contact ?? loadRow.contact_name, DOCUMENT_FALLBACKS.contactDispatcher) },
            { label: 'Pickup Address', value: formatAddress(loadRow.origin_address ?? loadRow.origin ?? loadRow.pickupAddress) },
            { label: 'Pickup Phone', value: formatPhone(loadRow.pickup_phone ?? loadRow.origin_phone ?? loadRow.contact_phone) },
            { label: 'Earliest Pickup', value: safeText(loadRow.pickupDate ?? loadRow.requestedPickup ?? loadRow.earliestPickup, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Pickup Instructions', value: safeText(loadRow.pickup_notes ?? loadRow.origin_notes ?? loadRow.notes, DOCUMENT_FALLBACKS.notProvided) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Destination" subtitle="Delivery or consignee information.">
        <DocumentFieldGrid
          items={[
            { label: 'Delivery Name', value: safeText(loadRow.delivery_contact ?? loadRow.destination_contact ?? loadRow.contact_name, DOCUMENT_FALLBACKS.contactDispatcher) },
            { label: 'Delivery Address', value: formatAddress(loadRow.destination_address ?? loadRow.destination ?? loadRow.deliveryAddress) },
            { label: 'Delivery Phone', value: formatPhone(loadRow.delivery_phone ?? loadRow.destination_phone ?? loadRow.contact_phone) },
            { label: 'Earliest / Latest Delivery', value: [safeText(loadRow.deliveryDate ?? loadRow.requestedDelivery ?? loadRow.earliestDelivery, DOCUMENT_FALLBACKS.tbd), safeText(loadRow.latestDelivery ?? loadRow.delivery_window_end, DOCUMENT_FALLBACKS.tbd)].join(' / ') },
            { label: 'Delivery Instructions', value: safeText(loadRow.delivery_notes ?? loadRow.destination_notes ?? loadRow.notes, DOCUMENT_FALLBACKS.notProvided) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Vehicle" subtitle="Pickup condition and handoff details.">
        <VehicleDocumentTable
          rows={vehicles}
          columns={[
            { label: 'Unit', render: (vehicle, index) => safeText(vehicle.unitNumber ?? vehicle.unit ?? index + 1, `Unit ${index + 1}`) },
            { label: 'Vehicle', render: (vehicle) => [safeText(vehicle.year, ''), safeText(vehicle.make, ''), safeText(vehicle.model, ''), safeText(vehicle.trim, '')].filter(Boolean).join(' ').trim() || DOCUMENT_FALLBACKS.notProvided },
            { label: 'VIN', render: (vehicle) => safeVin(vehicle.vin) },
            { label: 'Odometer', render: (vehicle) => safeText(vehicle.odometer ?? vehicle.mileage, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Bay / Stock / Owner', render: (vehicle) => [safeText(vehicle.bay, ''), safeText(vehicle.stockNumber ?? vehicle.stock_number, ''), safeText(vehicle.owner, '')].filter(Boolean).join(' / ') || DOCUMENT_FALLBACKS.notProvided },
            { label: 'Keys', render: (vehicle) => yesNo(vehicle.hasKeys, DOCUMENT_FALLBACKS.pending) },
            { label: 'Inop', render: (vehicle) => yesNo(vehicle.isInoperable ?? vehicle.inop, DOCUMENT_FALLBACKS.pending) },
            { label: 'Operable', render: (vehicle) => yesNo(vehicle.isRunning ?? vehicle.operable, DOCUMENT_FALLBACKS.pending) },
          ]}
          emptyMessage="No vehicle records were attached to this shipment."
        />
      </DocumentSection>

      <DocumentSection title="Damage Survey" subtitle="Condition notes captured at release and delivery.">
        <DocumentFieldGrid
          items={[
            { label: 'Damage Caused By', value: safeText(loadRow.damage_caused_by ?? loadRow.damageCausedBy ?? jobRow.damage_caused_by, DOCUMENT_FALLBACKS.notProvided) },
            { label: 'Driver Remarks', value: safeText(loadRow.driver_notes ?? jobRow.driver_notes ?? loadRow.notes, DOCUMENT_FALLBACKS.notProvided) },
            { label: 'Consignee Remarks', value: safeText(loadRow.consignee_notes ?? loadRow.destination_notes ?? DOCUMENT_FALLBACKS.notProvided, DOCUMENT_FALLBACKS.notProvided) },
            { label: 'Receiver / Dealer Remarks', value: safeText(loadRow.receiver_notes ?? loadRow.dealer_notes ?? DOCUMENT_FALLBACKS.notProvided, DOCUMENT_FALLBACKS.notProvided) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Signatures" subtitle="Pickup and delivery confirmation.">
        <SignatureBlock
          labels={[
            { label: 'Origin Signature', subtitle: 'Shipper or release agent' },
            { label: 'Destination Signature', subtitle: 'Consignee or receiver' },
            { label: 'Driver Signature', subtitle: 'Transport driver' },
            { label: 'Printed Name', subtitle: 'Signer full name' },
            { label: 'Date / Time', subtitle: 'Execution date and time' },
          ]}
        />
      </DocumentSection>

      <DocumentSection title="Driver Receipt" subtitle="Condensed handoff receipt.">
        <DocumentFieldGrid
          items={[
            { label: 'BOL / Order Number', value: safeText(jobRow.job_number ?? jobRow.jobNumber ?? loadRow.load_number ?? loadRow.loadNumber ?? jobRow.id, DOCUMENT_FALLBACKS.unknown) },
            { label: 'VIN', value: safeVin(vehicles[0]?.vin) },
            { label: 'Vehicle', value: [safeText(vehicles[0]?.year, ''), safeText(vehicles[0]?.make, ''), safeText(vehicles[0]?.model, ''), safeText(vehicles[0]?.trim, '')].filter(Boolean).join(' ').trim() || DOCUMENT_FALLBACKS.notProvided },
            { label: 'Keys Status', value: yesNo(vehicles[0]?.hasKeys, DOCUMENT_FALLBACKS.pending) },
            { label: 'Inop Status', value: yesNo(vehicles[0]?.isInoperable ?? vehicles[0]?.inop, DOCUMENT_FALLBACKS.pending) },
            { label: 'Receiving Signature', value: DOCUMENT_FALLBACKS.pending },
            { label: 'Date / Time', value: DOCUMENT_FALLBACKS.pending },
          ]}
          columns={2}
        />
      </DocumentSection>
    </div>
  )
}
